import { useState } from 'react';
import { api, getCsrfToken } from '../../lib/api';
import { addMonthsIso } from '../../../../shared/bookEdit';
import { formatDateAU } from '../../../../shared/format';

type Mode = null | 'tasks' | 'callDue';

/**
 * Actions on the selected households: route tasks to Next-Task.ai, download a
 * mail-merge list, or set Call Due in one go. Shown above the grid while
 * anything is selected.
 */
export function BulkBar({
  ids, today, canEdit, canExport, onDone, onClear,
}: {
  ids: string[];
  today: string;
  canEdit: boolean;
  canExport: boolean;
  onDone: (message: string, error?: boolean) => void;
  onClear: () => void;
}) {
  const [mode, setMode] = useState<Mode>(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<'call' | 'schedule'>('call');
  const [title, setTitle] = useState('Call {name}');
  const [due, setDue] = useState(today);
  const [note, setNote] = useState('');
  const [callDue, setCallDue] = useState(addMonthsIso(today, 1));
  const n = ids.length;
  const plural = n === 1 ? 'household' : 'households';

  async function sendTasks() {
    setBusy(true);
    try {
      const r = await api<{ data: { sent: number; failed: Array<{ reason: string }> } }>('/api/clients-bulk/next-task', {
        method: 'POST', body: JSON.stringify({ clientIds: ids, title, kind, dueDate: due, note: note || undefined }),
      });
      const f = r.data.failed.length;
      onDone(f ? `${r.data.sent} task${r.data.sent === 1 ? '' : 's'} sent to Next-Task, ${f} not sent (${r.data.failed[0].reason})` : `${r.data.sent} task${r.data.sent === 1 ? '' : 's'} sent to Next-Task`, f > 0 && r.data.sent === 0);
      setMode(null);
    } catch (e) { onDone(e instanceof Error ? e.message : 'Could not send tasks', true); } finally { setBusy(false); }
  }

  async function mailMerge() {
    setBusy(true);
    try {
      const token = getCsrfToken();
      const r = await fetch('/api/clients-bulk/mail-merge', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'x-csrf-token': token } : {}) },
        body: JSON.stringify({ clientIds: ids }),
      });
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mail-merge-${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      onDone(`Mail-merge list downloaded for ${n} ${plural}`);
    } catch (e) { onDone(e instanceof Error ? e.message : 'Export failed', true); } finally { setBusy(false); }
  }

  async function setCallDueAll(value: string | null) {
    setBusy(true);
    try {
      const r = await api<{ data: { updated: number } }>('/api/clients-bulk/call-due', { method: 'POST', body: JSON.stringify({ clientIds: ids, callDue: value }) });
      onDone(value ? `Call Due set to ${formatDateAU(value)} for ${r.data.updated} ${r.data.updated === 1 ? 'household' : 'households'}` : `Call Due cleared for ${r.data.updated}`);
      setMode(null);
    } catch (e) { onDone(e instanceof Error ? e.message : 'Could not update Call Due', true); } finally { setBusy(false); }
  }

  return (
    <div className="qa-book-bulk" role="region" aria-label="Bulk actions" data-testid="bulk-bar">
      <strong className="qa-mono">{n} selected</strong>
      {canEdit && <button type="button" className="qa-book-pop__btn" aria-expanded={mode === 'tasks'} onClick={() => setMode(mode === 'tasks' ? null : 'tasks')}>Tasks in Next-Task</button>}
      {canExport && <button type="button" className="qa-book-pop__btn" disabled={busy} onClick={mailMerge}>Mail-merge CSV</button>}
      {canEdit && <button type="button" className="qa-book-pop__btn" aria-expanded={mode === 'callDue'} onClick={() => setMode(mode === 'callDue' ? null : 'callDue')}>Set Call Due</button>}
      <button type="button" className="qa-book-pop__btn ml-auto" onClick={onClear}>Clear selection</button>

      {mode === 'tasks' && (
        <div className="w-full flex flex-wrap items-end gap-2 pt-2">
          <label className="text-xs">Type
            <select className="qa-select block mt-1 text-sm" value={kind} onChange={(e) => setKind(e.target.value as 'call' | 'schedule')}>
              <option value="call">Call</option>
              <option value="schedule">Schedule meeting</option>
            </select>
          </label>
          <label className="text-xs flex-1 min-w-[14rem]">Task title ({'{name}'} = client)
            <input className="qa-input block w-full mt-1 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="text-xs">Due
            <input type="date" className="qa-input block mt-1 text-sm" value={due} onChange={(e) => setDue(e.target.value)} />
          </label>
          <label className="text-xs flex-1 min-w-[12rem]">Note (optional)
            <input className="qa-input block w-full mt-1 text-sm" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="button" className="btn-primary text-sm" disabled={busy || !title.trim()} onClick={sendTasks}>
            Send {n} task{n === 1 ? '' : 's'}
          </button>
        </div>
      )}

      {mode === 'callDue' && (
        <div className="w-full flex flex-wrap items-end gap-2 pt-2">
          {[1, 3, 6, 12].map((m) => (
            <button key={m} type="button" className="qa-book-pop__btn" disabled={busy} onClick={() => setCallDueAll(addMonthsIso(today, m))}>+{m}m</button>
          ))}
          <label className="text-xs">Date
            <input type="date" className="qa-input block mt-1 text-sm" value={callDue} onChange={(e) => setCallDue(e.target.value)} />
          </label>
          <button type="button" className="btn-primary text-sm" disabled={busy || !callDue} onClick={() => setCallDueAll(callDue)}>Set for {n}</button>
          <button type="button" className="qa-book-pop__btn" disabled={busy} onClick={() => setCallDueAll(null)}>Clear</button>
        </div>
      )}
    </div>
  );
}
